import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import PrintProformaInvoice from '@/components/print/PrintProformaInvoice';
import { useToast } from '@/contexts/ToastContext';
import { type MockProformaInvoice } from '@/mocks/billing';
import { proformaService } from '@/services/proformaService';
import { getAllWarehouses, type WarehouseResponse } from '@/api/warehouse.api';
import { formatINR } from '@/utils/format';
import { useWarehouseStore } from '@/stores/warehouseStore';

const STATUS_STYLES: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-600 border-slate-200',
  SENT: 'bg-blue-50 text-blue-700 border-blue-200',
  CONVERTED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  CANCELLED: 'bg-red-50 text-red-700 border-red-200',
  EXPIRED: 'bg-orange-50 text-orange-700 border-orange-200',
};

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent to Customer',
  CONVERTED: 'Invoice Created',
  CANCELLED: 'Cancelled',
  EXPIRED: 'Expired',
};

export default function ProformaListPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { selectedWarehouseId: topWarehouseId } = useWarehouseStore();
  const [proformaList, setProformaList] = useState<MockProformaInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(topWarehouseId || 'ALL');
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showConvertModal, setShowConvertModal] = useState<MockProformaInvoice | null>(null);
  const [printProforma, setPrintProforma] = useState<MockProformaInvoice | null>(null);

  // Sync with global topbar warehouse selector
  useEffect(() => {
    if (topWarehouseId) {
      setSelectedWarehouseId(topWarehouseId);
    }
  }, [topWarehouseId]);

  // Load warehouses
  useEffect(() => {
    async function loadWarehouses() {
      try {
        const res = await getAllWarehouses();
        if (res && res.data && Array.isArray(res.data)) {
          setWarehouses(res.data.map((w: WarehouseResponse) => ({ id: w.id, name: w.name })));
        }
      } catch (e) {
        console.warn('Failed to load warehouses:', e);
      }
    }
    loadWarehouses();
  }, []);

  // Fetch proforma list from dynamic API
  const fetchProformaInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const data = await proformaService.listProformaInvoices({
        search: search || undefined,
        status: statusFilter !== 'All' ? statusFilter : undefined,
        warehouseId: selectedWarehouseId !== 'ALL' ? selectedWarehouseId : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      setProformaList(data || []);
    } catch (err) {
      console.error('Failed to fetch proforma invoices from backend:', err);
      toast.error('Failed to load proforma invoices');
      setProformaList([]);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, selectedWarehouseId, dateFrom, dateTo, toast]);

  useEffect(() => {
    fetchProformaInvoices();
  }, [fetchProformaInvoices]);

  // Summary cards
  const activeCount = proformaList.filter((p) => p.status === 'DRAFT' || p.status === 'SENT').length;
  const expectedAdvance = proformaList
    .filter((p) => p.status === 'SENT')
    .reduce((s, p) => s + (p.expectedAdvance || 0), 0);
  const balancePending = proformaList
    .filter((p) => p.status === 'SENT')
    .reduce((s, p) => s + (p.balanceDue || 0), 0);
  const convertedThisMonth = proformaList.filter((p) => p.status === 'CONVERTED').length;

  const handleSend = async (pi: MockProformaInvoice, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await proformaService.updateStatus(pi.id, 'SENT');
      toast.success(`Proforma ${pi.piNumber} marked as sent`);
      fetchProformaInvoices();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleCancel = async (pi: MockProformaInvoice, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await proformaService.updateStatus(pi.id, 'CANCELLED');
      toast.info(`Proforma ${pi.piNumber} cancelled`);
      fetchProformaInvoices();
    } catch (err) {
      toast.error('Failed to cancel proforma');
    }
  };

  const handleEdit = (pi: MockProformaInvoice, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate('/sales/proforma/new', {
      state: {
        editMode: true,
        piId: pi.id,
      },
    });
  };

  const handlePrint = (pi: MockProformaInvoice, e: React.MouseEvent) => {
    e.stopPropagation();
    setPrintProforma(pi);
  };

  const handleConvertToInvoice = (pi: MockProformaInvoice, e: React.MouseEvent) => {
    e.stopPropagation();
    setShowConvertModal(pi);
  };

  const handleConfirmConvert = () => {
    if (!showConvertModal) return;
    const pi = showConvertModal as any;
    navigate('/sales/invoices/new', {
      state: {
        fromProforma: {
          fromProforma: true,
          proformaId: pi.id,
          proformaInvoiceId: pi.id,
          piNumber: pi.piNumber || pi.pi_number,
          proformaNumber: pi.piNumber || pi.pi_number,
          customerId: pi.customerId || pi.customer_id,
          customerName: pi.customerName || pi.customer_name,
          customerGSTIN: pi.customerGSTIN || pi.customer_gstin,
          billingAddress: pi.billingAddress || pi.billing_address,
          shippingAddress: pi.shippingAddress || pi.shipping_address,
          warehouseId: pi.warehouseId || pi.warehouse_id,
          expectedAdvance: pi.expectedAdvance || pi.expected_advance,
          items: (pi.items || []).map((i: any) => ({
            itemId: i.itemId || i.item_id,
            itemCode: i.itemCode || i.item_code,
            itemName: i.itemName || i.item_name,
            hsnCode: i.hsnCode || i.hsn_code,
            qty: i.qty,
            unit: i.unit,
            rate: i.rate,
            discount: i.discountPercent || i.discount || i.discount_percent,
            taxRate: i.taxRate || i.tax_rate,
          })),
        },
      },
    });
    setShowConvertModal(null);
  };

  const actionBtnBase =
    'inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors';

  const renderActions = (pi: MockProformaInvoice) => {
    if (pi.status === 'DRAFT') {
      return (
        <div className="flex items-center gap-1.5 justify-end">
          <button onClick={(e) => handleSend(pi, e)} className={`${actionBtnBase} bg-blue-600 text-white hover:bg-blue-700`}>
            <i className="ri-send-plane-line text-xs" /> Send
          </button>
          <button onClick={(e) => handleEdit(pi, e)} className={`${actionBtnBase} bg-indigo-600 text-white hover:bg-indigo-700`}>
            <i className="ri-pencil-line text-xs" /> Edit
          </button>
          <button onClick={() => navigate(`/sales/proforma/${pi.id}`)} className={`${actionBtnBase} border border-slate-200 text-slate-600 hover:bg-slate-50`}>
            <i className="ri-eye-line text-xs" /> View
          </button>
          <button onClick={(e) => handlePrint(pi, e)} className={`${actionBtnBase} border border-slate-200 text-slate-600 hover:bg-slate-50`}>
            <i className="ri-printer-line text-xs" /> Print
          </button>
          <button onClick={(e) => handleCancel(pi, e)} className={`${actionBtnBase} border border-red-200 text-red-600 hover:bg-red-50`}>
            <i className="ri-close-circle-line text-xs" /> Cancel
          </button>
        </div>
      );
    }
    if (pi.status === 'SENT' || pi.status === 'APPROVED') {
      return (
        <div className="flex items-center gap-1.5 justify-end">
          <button onClick={(e) => handleConvertToInvoice(pi, e)} className={`${actionBtnBase} bg-indigo-600 text-white hover:bg-indigo-700`}>
            <i className="ri-file-copy-line text-xs" /> Convert to Invoice
          </button>
          <button onClick={(e) => handleEdit(pi, e)} className={`${actionBtnBase} border border-slate-200 text-slate-600 hover:bg-slate-50`}>
            <i className="ri-pencil-line text-xs" /> Edit
          </button>
          <button onClick={() => navigate(`/sales/proforma/${pi.id}`)} className={`${actionBtnBase} border border-slate-200 text-slate-600 hover:bg-slate-50`}>
            <i className="ri-eye-line text-xs" /> View
          </button>
          <button onClick={(e) => handlePrint(pi, e)} className={`${actionBtnBase} border border-slate-200 text-slate-600 hover:bg-slate-50`}>
            <i className="ri-printer-line text-xs" /> Print
          </button>
        </div>
      );
    }
    if (pi.status === 'CONVERTED') {
      return (
        <div className="flex items-center gap-1.5 justify-end">
          <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded border border-emerald-200">
            Converted → {pi.convertedToInvoiceNumber || 'INV'}
          </span>
          <button onClick={() => navigate(`/sales/proforma/${pi.id}`)} className={`${actionBtnBase} border border-slate-200 text-slate-600 hover:bg-slate-50`}>
            <i className="ri-eye-line text-xs" /> View
          </button>
          <button onClick={(e) => handlePrint(pi, e)} className={`${actionBtnBase} border border-slate-200 text-slate-600 hover:bg-slate-50`}>
            <i className="ri-printer-line text-xs" /> Print
          </button>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1.5 justify-end">
        <button onClick={(e) => handleEdit(pi, e)} className={`${actionBtnBase} border border-slate-200 text-slate-600 hover:bg-slate-50`}>
          <i className="ri-pencil-line text-xs" /> Edit
        </button>
        <button onClick={() => navigate(`/sales/proforma/${pi.id}`)} className={`${actionBtnBase} border border-slate-200 text-slate-600 hover:bg-slate-50`}>
          <i className="ri-eye-line text-xs" /> View
        </button>
      </div>
    );
  };

  return (
    <AppLayout>
      <div className="flex flex-col h-full bg-[#f8fafc]">
        <div className="flex-1 overflow-y-auto p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-bold text-slate-800">Proforma Invoices</h1>
              <p className="text-sm text-slate-500">Manage advance estimation invoices before billing</p>
            </div>
            <button
              onClick={() => navigate('/sales/proforma/new')}
              className="flex items-center gap-2 px-4 h-10 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer transition-colors shadow-sm"
            >
              <i className="ri-add-line" /> Create Proforma Invoice
            </button>
          </div>

          {/* Metric Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3.5 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
                <i className="ri-file-list-3-line text-indigo-600 text-lg" />
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Active Proformas</div>
                <div className="text-xl font-bold text-slate-800">{activeCount}</div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3.5 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                <i className="ri-hand-coin-line text-blue-600 text-lg" />
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Expected Advance</div>
                <div className="text-xl font-bold text-slate-800">{formatINR(expectedAdvance)}</div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3.5 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center shrink-0">
                <i className="ri-time-line text-amber-600 text-lg" />
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Balance Due</div>
                <div className="text-xl font-bold text-slate-800">{formatINR(balancePending)}</div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3.5 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
                <i className="ri-checkbox-circle-line text-emerald-600 text-lg" />
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Converted</div>
                <div className="text-xl font-bold text-slate-800">{convertedThisMonth}</div>
              </div>
            </div>
          </div>

          {/* Filters Bar */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 shadow-sm flex flex-wrap gap-4 items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-[280px]">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by PI # or Customer..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full h-9 pl-9 pr-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 text-slate-700"
                />
              </div>

              {/* Warehouse Filter */}
              <select
                value={selectedWarehouseId}
                onChange={(e) => setSelectedWarehouseId(e.target.value)}
                className="h-9 px-3 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 text-slate-700 cursor-pointer"
              >
                <option value="ALL">All Warehouses</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>

              <div className="flex items-center gap-2">
                {['All', 'DRAFT', 'SENT', 'CONVERTED', 'CANCELLED'].map((st) => (
                  <button
                    key={st}
                    onClick={() => setStatusFilter(st)}
                    className={`h-9 px-3 rounded-lg text-xs font-medium cursor-pointer transition-colors ${
                      statusFilter === st
                        ? 'bg-slate-800 text-white'
                        : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {st === 'All' ? 'All Status' : STATUS_LABELS[st] || st}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 px-3 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 text-slate-700"
              />
              <span className="text-xs text-slate-400">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 px-3 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 text-slate-700"
              />
            </div>
          </div>

          {/* Table */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-slate-400">
                <i className="ri-loader-4-line text-2xl animate-spin inline-block mb-2" />
                <p className="text-sm">Loading proforma invoices...</p>
              </div>
            ) : proformaList.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <i className="ri-inbox-line text-3xl mb-2 inline-block" />
                <p className="text-sm font-medium">No proforma invoices found</p>
                <p className="text-xs mt-1">Try adjusting your filters or create a new proforma invoice</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider">
                      <th className="py-3 px-4">PI # / Date</th>
                      <th className="py-3 px-4">Customer</th>
                      <th className="py-3 px-4">Total Amount</th>
                      <th className="py-3 px-4">Exp. Advance</th>
                      <th className="py-3 px-4">Balance Due</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                    {proformaList.map((pi) => {
                      const stStyle = STATUS_STYLES[pi.status] || STATUS_STYLES.DRAFT;
                      const stLabel = STATUS_LABELS[pi.status] || pi.status;

                      return (
                        <tr
                          key={pi.id}
                          onClick={() => navigate(`/sales/proforma/${pi.id}`)}
                          className="hover:bg-slate-50/80 cursor-pointer transition-colors"
                        >
                          <td className="py-3.5 px-4">
                            <span className="font-bold text-slate-800 cursor-pointer hover:underline" onClick={() => navigate(`/sales/proforma/${pi.id}`)}>{pi.piNumber}</span>
                            <div className="text-[11px] text-slate-400 font-normal">{pi.date}</div>
                          </td>
                          <td className="py-3.5 px-4">
                            <div className="font-semibold text-slate-800">{pi.customerName}</div>
                            {pi.customerGSTIN && (
                              <div className="text-[11px] text-slate-400 font-normal">GSTIN: {pi.customerGSTIN}</div>
                            )}
                          </td>
                          <td className="py-3.5 px-4 font-bold text-slate-900">{formatINR(pi.totalAmount)}</td>
                          <td className="py-3.5 px-4 text-blue-600 font-semibold">
                            {pi.expectedAdvance ? formatINR(pi.expectedAdvance) : '—'}
                          </td>
                          <td className="py-3.5 px-4 text-amber-600 font-semibold">
                            {pi.balanceDue ? formatINR(pi.balanceDue) : formatINR(pi.totalAmount)}
                          </td>
                          <td className="py-3.5 px-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${stStyle}`}>
                              {stLabel}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 text-right">{renderActions(pi)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Convert Modal */}
        {showConvertModal && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md p-6">
              <div className="flex items-center gap-3 text-indigo-600 mb-3">
                <i className="ri-file-copy-line text-2xl" />
                <h3 className="text-base font-bold text-slate-800">Convert to Sales Invoice</h3>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Are you sure you want to convert Proforma <strong>{showConvertModal.piNumber}</strong> to a Tax Invoice?
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-1.5 text-slate-600 mb-6">
                <div>
                  <strong>Customer:</strong> {showConvertModal.customerName}
                </div>
                <div>
                  <strong>Proforma Amount:</strong> {formatINR(showConvertModal.totalAmount)}
                </div>
                <div>
                  <strong>Expected Advance:</strong> {formatINR(showConvertModal.expectedAdvance)}
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setShowConvertModal(null)}
                  className="px-4 h-9 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmConvert}
                  className="px-4 h-9 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer"
                >
                  Proceed to Invoice
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Print Modal */}
        {printProforma && <PrintProformaInvoice proforma={printProforma} onClose={() => setPrintProforma(null)} />}
      </div>
    </AppLayout>
  );
}